import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { AlertCircle, Mail, CheckCircle, RefreshCw, HelpCircle, ShieldAlert } from 'lucide-react'
import api from '../lib/api'

/**
 * Login Page - Step 1 of magic link auth flow
 * 1. User enters email
 * 2. System sends magic link
 * 3. User sees "check your email" message
 *
 * After clicking magic link, user is directed to /auth/verify/:token
 * which handles tenant selection and PIN verification
 *
 * Handles unverified email case:
 * - If email is not verified, shows message with option to resend verification
 */
export default function Login() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [linkSent, setLinkSent] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [showHelp, setShowHelp] = useState(false)
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [verificationResent, setVerificationResent] = useState(false)
  const [isResendingVerification, setIsResendingVerification] = useState(false)

  const { requestMagicLink, resendMagicLink } = useAuth()

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setEmailNotVerified(false)
    setVerificationResent(false)
    setIsLoading(true)

    try {
      await requestMagicLink(email)
      setLinkSent(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send sign-in link'
      // Check if this is an unverified email error
      if (errorMessage.toLowerCase().includes('verify your email') ||
          errorMessage.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true)
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (isResendingVerification || resendCooldown > 0) return

    setIsResendingVerification(true)
    setError('')

    try {
      const result = await api.resendVerificationEmail(email)
      setVerificationResent(true)

      if (!result.already_verified) {
        // Start 60 second cooldown
        setResendCooldown(60)
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email')
    } finally {
      setIsResendingVerification(false)
    }
  }

  const handleTryAgain = () => {
    setLinkSent(false)
    setEmail('')
    setError('')
    setResendCooldown(0)
    setShowHelp(false)
    setEmailNotVerified(false)
    setVerificationResent(false)
  }

  const handleResendLink = async () => {
    if (resendCooldown > 0 || isResending) return

    setError('')
    setIsResending(true)

    try {
      await resendMagicLink(email)
      // Start 60 second cooldown after successful resend
      setResendCooldown(60)
      const timer = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend sign-in link')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)', backgroundSize: '48px 48px'}} />
      <div className="w-full max-w-md relative">
        <div className="bg-white rounded-2xl shadow-elevated p-8 border border-white/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="w-20 h-20" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-primary-700 tracking-display">Clarantis</h1>
            <p className="text-gray-500 mt-1">
              {emailNotVerified
                ? 'Verify your email'
                : linkSent
                  ? 'Check your email'
                  : 'Sign in to your account'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Email Not Verified State */}
          {emailNotVerified ? (
            <div className="space-y-6">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                <ShieldAlert className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Email Not Verified</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Please verify your email address before logging in. Check your inbox for the verification link.
                  </p>
                </div>
              </div>

              {verificationResent && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Verification Email Sent!</p>
                    <p className="text-sm text-green-700 mt-1">
                      We sent a new verification link to <strong>{email}</strong>. Please check your inbox.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-center mb-3">
                  <Mail className="h-8 w-8 text-primary-600" />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Verification links expire after 24 hours. If you can't find the email, check your spam folder or request a new one.
                </p>
              </div>

              {/* Resend Verification Button */}
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0 || isResendingVerification}
                className="w-full btn-primary py-3 text-base inline-flex items-center justify-center"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isResendingVerification ? 'animate-spin' : ''}`} />
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : isResendingVerification
                    ? 'Sending...'
                    : 'Resend Verification Email'
                }
              </button>

              <button
                type="button"
                onClick={handleTryAgain}
                className="w-full text-sm text-primary-600 hover:text-primary-700"
              >
                Use a different email address
              </button>
            </div>
          ) : linkSent ? (
            <div className="space-y-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-800 font-medium">Sign-in link sent!</p>
                  <p className="text-sm text-green-700 mt-1">
                    We sent a sign-in link to <strong>{email}</strong>. Click the link in your email to continue.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-center mb-3">
                  <Mail className="h-8 w-8 text-primary-600" />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  The link will expire in 10 minutes. If you don't see the email, check your spam folder.
                </p>
              </div>

              {/* Resend Link Button */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleResendLink}
                  disabled={resendCooldown > 0 || isResending}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isResending ? 'animate-spin' : ''}`} />
                  {resendCooldown > 0
                    ? `Resend link in ${resendCooldown}s`
                    : isResending
                      ? 'Sending...'
                      : "Didn't receive email? Resend link"
                  }
                </button>
              </div>

              {/* Help Section */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowHelp(!showHelp)}
                  className="w-full flex items-center justify-center text-sm text-gray-500 hover:text-gray-700"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  {showHelp ? 'Hide troubleshooting tips' : 'Need help?'}
                </button>

                {showHelp && (
                  <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Troubleshooting tips:</p>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>Check your spam or junk folder</li>
                      <li>Verify you entered the correct email address</li>
                      <li>Wait a few minutes for the email to arrive</li>
                      <li>Try resending the link (max 3 per 5 minutes)</li>
                      <li>Contact your administrator if issues persist</li>
                    </ul>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleTryAgain}
                className="w-full text-sm text-primary-600 hover:text-primary-700"
              >
                Use a different email address
              </button>
            </div>
          ) : (
            /* Email Input Form */
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="Enter your email"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-3 text-base"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending link...
                  </span>
                ) : (
                  'Continue with Email'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                We'll send you a secure sign-in link. No password required.
              </p>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-white/60 font-heading tracking-wide">
          Governance, Risk & Compliance Platform
        </p>
      </div>
    </div>
  )
}
